import { DatePipe, KeyValue, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnChanges, output, SimpleChanges } from '@angular/core';
import { MotifButtonModule, MotifChipModule } from '@ey-xd/ng-motif';
import { DATE_TIME_FORMAT, JSONValue, PropertyFilter } from '@models';
import { FieldType, FilterCriterion } from '@models/filter.model';
import { TranslateModule } from '@ngx-translate/core';

type FilterChipsLocation = 'grid' | 'filter-modal';

@Component({
  selector: 'app-filter-chips',
  standalone: true,
  imports: [MotifChipModule, TranslateModule, MotifButtonModule, KeyValuePipe],
  providers: [DatePipe],
  templateUrl: './filter-chips.component.html',
  styleUrl: './filter-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipsComponent implements OnChanges {
  filters = input<PropertyFilter[]>([]);
  filterCriteria = input<FilterCriterion[]>([]);
  showTitle = input<boolean>(false);
  removeChip = output<PropertyFilter[]>();
  removeAll = output();
  filterChips = new Map<string, JSONValue[]>();
  // Added: map to keep original filterCriteria ordering
  private orderIndex = new Map<string, number>();
  location = input<FilterChipsLocation>('grid');
  #datePipe: DatePipe = inject(DatePipe);

  public onRemoveChip(key: KeyValue<string, JSONValue[]>, chipValue: JSONValue): void {
    const removedFilter = this.filters().find((f: PropertyFilter) => {
      return this.getFilterLabel(f) === key.key && this.getPrettyFilterValue(f) === chipValue;
    });
    this.removeChip.emit([removedFilter as PropertyFilter]);
  }

  public onRemoveAll(): void {
    this.removeAll.emit();
  }

  public getFilterLabel(filter: PropertyFilter): string {
    let label = '';
    const clonedFilter = structuredClone(filter);
    this.filterCriteria().forEach((f: FilterCriterion) => {
      if (f.type === FieldType.Date) {
        if (f.field === clonedFilter.field && clonedFilter.conditionPropertyOperator === f.operation) {
          label = f.label.toString();
        }
      } else if (f.field === clonedFilter.field) {
        label = f.label.toString();
      }
    });
    return label;
  }

  public getPrettyFilterValue(key: PropertyFilter): JSONValue {
    const filter = this.filterCriteria().find((f: FilterCriterion) => f.key === key.propertyName);

    if (!filter) {
      return key.value;
    }
    switch (filter?.type) {
      case FieldType.Date:
        return this.#datePipe.transform(key.value as string, DATE_TIME_FORMAT.DATE_FILTER_FORMAT) as string;
      case FieldType.Select:
      case FieldType.MultipleSelect:
        return filter.options?.find(option => option.value === key.value)?.displayName || key.value;
      case FieldType.Lookup:
      case FieldType.MultipleLookup:
        return (key.value as { displayName: string }).displayName || key.value;
      default:
        return (key.value as string).replace(/^%|%$/g, '');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterCriteria']) {
      this.orderIndex.clear();
      this.filterCriteria().forEach((fc: FilterCriterion, idx: number) => {
        this.orderIndex.set(fc.label.toString(), idx);
      });
    }
    if (changes['filters']) {
      this.filterChips.clear();
      this.filters().forEach((f: PropertyFilter): void => {
        const filterKey = this.getFilterLabel(f);
        if (this.filterChips.has(filterKey)) {
          this.filterChips.get(filterKey)?.push(this.getPrettyFilterValue(f));
        } else {
          this.filterChips.set(filterKey, [this.getPrettyFilterValue(f)]);
        }
      });
    }
  }

  // Comparator passed to keyvalue pipe to preserve criteria order
  chipsCompare = (a: KeyValue<string, JSONValue[]>, b: KeyValue<string, JSONValue[]>) => {
    return (this.orderIndex.get(a.key) ?? 0) - (this.orderIndex.get(b.key) ?? 0);
  };

  showClearAll() {
    return this.location() === 'grid' && this.filterChips.size > 0;
  }
}
